import { useEffect } from 'react';
import { Button, Card, Col, Form, Input, Row, Space, Spin, Switch, message } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { personsApi, type PersonInput } from '../../../api/endpoints/persons';
import { AccountSelect } from '../../../components/common/AccountSelect';
import { PageHeader } from '../../../components/common/PageHeader';
import { LookupAutoComplete } from '../../../components/common/LookupAutoComplete';

const schema = z.object({
  firstName: z.string().max(60).optional().or(z.literal('')),
  lastName: z.string().min(1, 'Apellido obligatorio').max(60),
  accountId: z.number().int().optional().nullable(),
  jobTitle: z.string().max(60).optional().or(z.literal('')),
  department: z.string().max(60).optional().or(z.literal('')),
  email: z.string().email('Correo inválido').optional().or(z.literal('')),
  emailAlt: z.string().email('Correo inválido').optional().or(z.literal('')),
  phone: z.string().max(30).optional().or(z.literal('')),
  mobile: z.string().max(30).optional().or(z.literal('')),
  isDecisionMaker: z.boolean().optional(),
  source: z.string().max(60).optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

function sanitize(values: FormValues): PersonInput {
  const out: Partial<PersonInput> = {};
  (Object.keys(values) as Array<keyof FormValues>).forEach((k) => {
    const v = values[k];
    if (v === '' || v === undefined || v === null) return;
    (out as Record<string, unknown>)[k] = v;
  });
  return { ...out, lastName: values.lastName } as PersonInput;
}

export function PersonEditPage(): JSX.Element {
  const { id } = useParams();
  const personId = id ? parseInt(id, 10) : null;
  const isEdit = personId !== null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const presetAccountId = searchParams.get('accountId');

  const { data, isLoading } = useQuery({
    queryKey: ['person', personId],
    queryFn: () => personsApi.findOne(personId!),
    enabled: isEdit,
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      accountId: presetAccountId ? parseInt(presetAccountId, 10) : null,
      jobTitle: '',
      department: '',
      email: '',
      emailAlt: '',
      phone: '',
      mobile: '',
      isDecisionMaker: false,
      source: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        firstName: data.firstName ?? '',
        lastName: data.lastName,
        accountId: data.accountId ?? null,
        jobTitle: data.jobTitle ?? '',
        department: data.department ?? '',
        email: data.email ?? '',
        emailAlt: data.emailAlt ?? '',
        phone: data.phone ?? '',
        mobile: data.mobile ?? '',
        isDecisionMaker: data.isDecisionMaker,
        source: data.source ?? '',
        notes: data.notes ?? '',
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = sanitize(values);
      if (isEdit) return personsApi.update(personId, payload);
      return personsApi.create(payload);
    },
    onSuccess: (saved) => {
      message.success(isEdit ? 'Contacto actualizado' : 'Contacto creado');
      void queryClient.invalidateQueries({ queryKey: ['persons'] });
      void queryClient.invalidateQueries({ queryKey: ['person', saved.id] });
      navigate(`/contactos/${saved.id}`);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo guardar el contacto';
      message.error(msg);
    },
  });

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar contacto' : 'Nuevo contacto'}
        crumbs={[
          { title: 'Contactos', to: '/contactos' },
          { title: isEdit ? `${data?.firstName ?? ''} ${data?.lastName ?? ''}` : 'Nuevo' },
        ]}
      />

      <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
        <Card title="Datos personales" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Nombre(s)">
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="lastName"
                control={control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Apellidos"
                    required
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Cuenta">
                    <AccountSelect value={field.value} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="jobTitle"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Puesto">
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Departamento">
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="source"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Fuente">
                    <LookupAutoComplete
                      listName="person_source"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Ej. Referencia, Sitio web…"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="isDecisionMaker"
                control={control}
                render={({ field }) => (
                  <Form.Item label="¿Toma decisiones?">
                    <Switch checked={field.value ?? false} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Contacto" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Correo"
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="emailAlt"
                control={control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Correo alterno"
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Teléfono">
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Móvil">
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Notas" style={{ marginBottom: 16 }}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <Form.Item>
                <Input.TextArea {...field} rows={4} />
              </Form.Item>
            )}
          />
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear contacto'}
          </Button>
          <Button onClick={() => navigate(-1)}>Cancelar</Button>
        </Space>
      </Form>
    </>
  );
}
